// Copyright ©️ 2026 eVolve MEP, LLC

import { dateOnly, formatDate } from '@evolvemep/api-ts';
import { Anchor, Flex, Stack, Text } from '@mantine/core';

import { EvolveIcon } from 'assets/icons/EvolveIcon';
import { CustomPropertiesDetailSection } from 'components/Mantine/CustomProperties/CustomPropertiesDetailSection';
import { DatePickerWrapped } from 'components/Mantine/DateInputWrapped';
import { DetailSection } from 'components/Mantine/DetailPage/DetailSection';
import { SmallDetailItem } from 'components/Mantine/DetailPage/SmallDetailItem';
import { MultiUserSelect } from 'components/Mantine/MultiUserSelect';
import { EvolveLink } from 'components/Mantine/Navigation/EvolveLink';
import { TextareaWrapped, TextInputWrapped } from 'components/Mantine/TextInputWrapped';
import { UserInfoBadge } from 'components/Mantine/UserInfoBadge';
import { EditableCell } from 'helpers/ag-grid/EditableCells/EditableCell';
import { looksLikeUrl } from 'helpers/stringFunctions';
import { EditableShipmentStatusBadge } from 'modules/Shop/Shipments/ShipmentDetail/EditableShipmentStatusBadge';

import type { ShipmentDetailPageProps } from './ShipmentDetailWrapper';
import { useUpdateShipment } from './useUpdateShipment';

export const ShipmentSidePane = ({ shipment, onShipmentUpdated }: ShipmentDetailPageProps) => {
  const { updateShipment, canEditShipment, mutationRunning } = useUpdateShipment({ shipment, onShipmentUpdated });

  return (
    <Stack>
      <DetailSection title="Status">
        <EditableShipmentStatusBadge
          shipment={shipment}
          editable={canEditShipment('editStatus')}
          onShipmentUpdated={onShipmentUpdated}
          size="xxl"
          fullWidth
        />
      </DetailSection>

      <DetailSection
        title="Shipping Assignees"
        rightSection={
          <EditableCell
            Editor={MultiUserSelect}
            initialValue={shipment.assignees}
            renderIcon="Add"
            onSave={(assignees) => updateShipment({ assignees: assignees?.map((assignee) => assignee.userId) ?? [] })}
            editable={canEditShipment('editAssignees')}
          />
        }
      >
        <EditableCell
          Editor={MultiUserSelect}
          initialValue={shipment.assignees}
          renderValue={
            <Stack gap="xs">
              {shipment.assignees.map((assignee) => (
                <UserInfoBadge key={assignee.userId} user={assignee} />
              ))}
            </Stack>
          }
          emptyState={
            <>
              <EvolveIcon icon="EmptyCircle" size="sm" />
              Add assignee
            </>
          }
          showEmptyState={shipment.assignees.length === 0}
          onSave={(assignees) => updateShipment({ assignees: assignees?.map((assignee) => assignee.userId) ?? [] })}
          editable={canEditShipment('editAssignees')}
        />
      </DetailSection>

      <DetailSection title="Shipping Info">
        <EditableCell
          label="Ship To"
          Editor={TextareaWrapped}
          editorProps={{ maxLength: 256 }}
          initialValue={shipment.shipTo}
          onSave={(shipTo) => updateShipment({ shipTo })}
          renderValue={<Text style={{ whiteSpace: 'pre-wrap' }}>{shipment.shipTo}</Text>}
          editable={canEditShipment('editShippingInfo')}
        />
        <EditableCell
          label="Tracking #"
          Editor={TextInputWrapped}
          editorProps={{ maxLength: 2048 }}
          initialValue={shipment.trackingNumber}
          onSave={(trackingNumber) => updateShipment({ trackingNumber })}
          renderValue={
            looksLikeUrl(shipment.trackingNumber) ? (
              <Anchor href={shipment.trackingNumber} target="_blank" rel="noopener noreferrer" fz="sm">
                {shipment.trackingNumber}
              </Anchor>
            ) : (
              shipment.trackingNumber
            )
          }
          editable={canEditShipment('editShippingInfo')}
        />
      </DetailSection>

      <DetailSection title="Dates">
        <EditableCell
          label="Ship By"
          labelTooltip="The requested date that the shipment must be shipped by."
          Editor={DatePickerWrapped}
          initialValue={shipment.shipByDate}
          editorProps={{ minDate: dateOnly(shipment.createdOn) }}
          onSave={(shipByDate) => updateShipment({ shipByDate })}
          renderValue={formatDate(shipment.shipByDate, 'MM/dd/yyyy')}
          closeOnChange
          editable={canEditShipment('editDates')}
        />
        <EditableCell
          label="Scheduled Ship"
          labelTooltip="The date which has been coordinated as the actual shipment date."
          Editor={DatePickerWrapped}
          initialValue={shipment.scheduledShipDate}
          editorProps={{ minDate: dateOnly(shipment.createdOn) }}
          onSave={(scheduledShipDate) => updateShipment({ scheduledShipDate })}
          renderValue={formatDate(shipment.scheduledShipDate, 'MM/dd/yyyy')}
          closeOnChange
          editable={canEditShipment('editDates')}
        />
        <SmallDetailItem label="Shipped">{formatDate(shipment.shippedOn, 'MM/dd/yyyy')}</SmallDetailItem>
        <SmallDetailItem label="Delivered">{formatDate(shipment.deliveredOn, 'MM/dd/yyyy')}</SmallDetailItem>
      </DetailSection>

      <CustomPropertiesDetailSection
        entity={shipment}
        disabled={mutationRunning}
        customPropertyTypeName="Shipment"
        onUpdateCustomProperty={(customPropertyId, value) =>
          updateShipment({
            customProperties: [{ customPropertyId, value }],
          })
        }
      />

      {shipment.relatedWorkOrders.length > 0 && (
        <DetailSection title="Contains Items From">
          <Stack gap={4}>
            {shipment.relatedWorkOrders.map((workOrder) => (
              <EvolveLink
                key={workOrder.workOrderId}
                to={`/work-orders/${workOrder.workOrderId}`}
                from={shipment.identifier}
              >
                <Flex align="center" gap="xs">
                  <EvolveIcon icon="WorkOrder" light />
                  {workOrder.workOrderName}
                </Flex>
              </EvolveLink>
            ))}
          </Stack>
        </DetailSection>
      )}

      <DetailSection title="Properties">
        <SmallDetailItem label="Shipping Order Id">{shipment.identifier}</SmallDetailItem>
        <SmallDetailItem label="Facility">{shipment.facilityName}</SmallDetailItem>
        <SmallDetailItem label="Project">{shipment.projectName}</SmallDetailItem>
        <SmallDetailItem label="Created By">
          <UserInfoBadge user={shipment.createdByUser} />
        </SmallDetailItem>
      </DetailSection>
    </Stack>
  );
};
